import React, { useEffect, useState } from "react";
import "../../style/components.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import {
  TIMEPERIOD_SALES,
  TIMEPERIOD_ONBOARD_DEVICE,
  TIMEPERIOD_TOPSELLING,
} from "../../constants/Common";
import EmptyTableRecord from "../layout/EmptyTableRecord";
import DropdownBox from "../layout/DropdownBox";
import { useBrands } from "../../utils/useBrand";

const StockProduct = ({
  brandNames,
  salesProducts,
  topSellingProductsData,
  onboardedDevices,
  onBrandSalesChange,
  onBrandTopSellingChange
}) => {
  const { brands, loading, hasMore, loadMore, cleanup } = useBrands();
  const [selectedBrandSales, setSelectedBrandSales] = useState("All");
  const [selectedBrandTopSelling, setSelectedBrandTopSelling] = useState("All");
  const [selectedTimePeriod_Sales, setSelectedTimePeriod_Sales] = useState(
    `${TIMEPERIOD_SALES[0].toLowerCase()}`
  );
  const [selectedTimePeriod_TopSelling, setSelectedTimePeriod_TopSelling] =
    useState(`${TIMEPERIOD_TOPSELLING[0].toLowerCase()}`);
  const [
    selectedTimePeriod_OnboardDevices,
    setSelectedTimePeriod_OnboardDevices,
  ] = useState(`${TIMEPERIOD_ONBOARD_DEVICE[0].toLowerCase()}`);

  useEffect(() => {
    if (brands.length === 0) loadMore();
    return cleanup;
  }, []);

  const handleDropdownScroll = (e) => {
    const el = e.target;
    const nearBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 40;
    if (nearBottom && hasMore && !loading) {
      loadMore();
    }
  };

  const handleBrandSalesChange = (val) => {
    setSelectedBrandSales(val);
    const brandId = val === "All" ? undefined : val;
    onBrandSalesChange(brandId);
  };

  const handleBrandTopSellingChange = (val) => {
    setSelectedBrandTopSelling(val);
    const brandId = val === "All" ? undefined : val;
    onBrandTopSellingChange(brandId);
  };
  const salesData = salesProducts[selectedTimePeriod_Sales] || [];
  const topSellingData =
    topSellingProductsData[selectedTimePeriod_TopSelling] || [];

  // Filter onboard devices based on selected time period
  const selectedOnboardedDevices = Object.entries(
    onboardedDevices[selectedTimePeriod_OnboardDevices] || {}
  ).map(([deviceName, count]) => ({
    deviceName,
    count,
  }));

  // Get total count for progress bar calculation
  const totalDevices = selectedOnboardedDevices.reduce(
    (sum, device) => sum + device.count,
    0
  );
  function getCalculatedWidth() {
    const productCount = salesData.length;
    if (productCount <= 10) {
    return Math.max(600, productCount * 80);
    } else {
      return productCount * 60;
    }
  }
  // Progress bar component
  const ProgressBar = ({ label, value, totalValue }) => {
    const percentage = (value / totalValue) * 100;
    return (
      <div className="w-full">
        <div className="flex gap-5 justify-between">
          <label className="block text-base font-semibold text-text1 ">
            {label}
          </label>
          <div className="flex text-base text-text1 font-semibold">
            <span>{value}</span>
          </div>
        </div>
        <div className="h-3.5 bg-[#D9D9D9] rounded-full mt-2.5">
          <div
            className="h-full bg-primary rounded-full"
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4 sm:px-10">
      {/* Sales vs Products */}
      <div className="p-5 border border-gray-300 rounded-lg min-h-[400px]">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-2 w-full sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold whitespace-nowrap">
              Sales vs Products
            </h2>
            <div className="flex gap-3 w-full sm:w-auto">
              <div className="w-32">
                <div className="relative z-[120]">
                  <DropdownBox
                    optionList={TIMEPERIOD_SALES.map((p) => ({
                      label:
                        p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
                      value: p.toLowerCase()
                    }))}
                    styleName="rounded px-1 text-[12px] font-normal text-text1 focus:outline-none focus:ring-0 w-full cursor-pointer"
                    value={selectedTimePeriod_Sales}
                    displayKey="label"
                    valueKey="value"
                    isObjectData={true}
                    handleChange={(val) => setSelectedTimePeriod_Sales(val)}
                    compact={false}
                    dropdownMenuClassName="absolute left-0 top-full z-[120] w-full sm:static sm:z-10"
                  />
                </div>
              </div>
              <div className="w-36">
                <DropdownBox
                  optionList={[{ name: "All", id: "All" }, ...brands]}
                  onMenuScroll={handleDropdownScroll}
                  styleName="rounded px-1 text-[12px] font-normal text-text1 focus:outline-none focus:ring-0 w-full cursor-pointer"
                  value={selectedBrandSales}
                  displayKey="name"
                  valueKey="id"
                  isObjectData={true}
                  handleChange={(val) => handleBrandSalesChange(val)}
                  compact={false}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {salesData.length > 0 ? (
            <div className="mt-6 w-full h-[400px] flex relative">
              {/* Fixed Y Axis */}
              <div
                className="z-30 bg-white"
                style={{
                  minWidth: "60px",
                  maxWidth: "60px",
                  width: "60px",
                  flexShrink: 0
                }}
              >
                <ResponsiveContainer width="100%" height={354}>
                  <BarChart
                    data={salesData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                  >
                    <YAxis tickCount={8} interval={0} />
                    <Bar dataKey="total" fill="transparent" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Horizontally scrollable chart */}
              <div
                className="overflow-x-auto"
                style={{ width: "calc(100% - 60px)" }}
              >
                <ResponsiveContainer height={380} width={getCalculatedWidth()}>
                  <BarChart
                    data={salesData}
                    barSize={40}
                    margin={{ top: 10, right: 30, left: 10, bottom: 6 }}
                  >
                    <XAxis
                      dataKey="name"
                      stroke="#656565"
                      tickCount={10}
                      interval={0}
                      minTickGap={10}
                      tick={{ fontSize: 8 }}
                      tickFormatter={(value) =>
                        value.length > 10
                          ? value.substring(0, 10) + "..."
                          : value
                      }
                    />
                    <YAxis hide={true} tickCount={8} interval={0} />
                    <Tooltip
                      cursor={{ fill: "rgba(5, 226, 126, 0.0)" }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white border border-gray-200 p-2 rounded">
                              <p className="font-semibold text-gray-900">
                                {payload[0].payload.name}
                              </p>
                              <p className="text-gray-900">
                                Sales:{" "}
                                <span className="font-semibold">
                                  {payload[0].value}
                                </span>
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="total" fill="#05E27E" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center min-h-[300px] rounded-md">
              <EmptyTableRecord />
            </div>
          )}
        </div>
        {salesData.length > 0 && (
          <div className="justify-center text-center mt-2">
            <div className="justify-center text-center text-sm">
              Plan & Packages
            </div>
            <div className="flex justify-center align-bottom">
              <div className="bg-primary w-4 h-3 mt-2"></div>
              <div>
                <span
                  style={{
                    color: "#656565",
                    padding: "5px",
                    fontSize: "12px"
                  }}
                >
                  Sales (Units)
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Top Selling Products */}
      <div className="p-5 border border-gray-300 rounded-lg min-h-[400px]">
        <div className="flex justify-between items-center gap-2">
          <div className="flex flex-col gap-2 w-full sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold">🔥 Top selling products</h2>
            <div className="flex gap-3 w-full sm:w-auto">
              <div className="w-32">
                <DropdownBox
                  optionList={TIMEPERIOD_TOPSELLING.map((p) => ({
                    label: p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
                    value: p.toLowerCase()
                  }))}
                  styleName="rounded px-1 text-[12px] font-normal text-text1 focus:outline-none focus:ring-0 w-full cursor-pointer"
                  value={selectedTimePeriod_TopSelling}
                  displayKey="label"
                  valueKey="value"
                  isObjectData={true}
                  handleChange={(val) => setSelectedTimePeriod_TopSelling(val)}
                  compact={false}
                />
              </div>
              <div className="w-36">
                <DropdownBox
                  optionList={[{ name: "All", id: "All" }, ...brands]}
                  onMenuScroll={handleDropdownScroll}
                  styleName="rounded px-1 text-[12px] font-normal text-text1 focus:outline-none focus:ring-0 w-full cursor-pointer"
                  value={selectedBrandTopSelling}
                  displayKey="name"
                  valueKey="id"
                  isObjectData={true}
                  handleChange={(val) => handleBrandTopSellingChange(val)}
                  compact={false}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6">
          {topSellingData.length > 0 ? (
            <div className="max-h-[400px] overflow-y-auto pr-2">
              {topSellingData.map((product) => (
                <div
                  key={product._id}
                  className="flex items-center gap-6 pb-6 pr-2"
                >
                  <div className="flex flex-col justify-center flex-grow">
                    <h3 className="font-medium text-text1 text-base">
                      {product.name}
                    </h3>
                    <p className="text-text2 text-sm">Sold: {product.total}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-semibold">
                      € {Number(product.totalSalesAmount || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center min-h-[300px] rounded-md">
              <EmptyTableRecord />
            </div>
          )}
        </div>
      </div>



      {/* Onboard Devices */}
      <div className="p-5 border border-gray-300 rounded-lg min-h-[400px] sm:col-span-2">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">On-boarded devices</h2>
          <div className="flex space-x-4 gap-3">
            <div className="w-32">
              <DropdownBox
                optionList={TIMEPERIOD_ONBOARD_DEVICE.map((p) => ({
                  label: p,
                  value: p.toLowerCase(),
                }))}
                styleName="rounded px-1 text-[12px] font-normal text-text1 focus:outline-none focus:ring-0 w-full cursor-pointer"
                value={selectedTimePeriod_OnboardDevices}
                displayKey="label"
                valueKey="value"
                isObjectData={true}
                handleChange={(val) =>
                  setSelectedTimePeriod_OnboardDevices(val)
                }
                compact={false}
              />
            </div>
          </div>
        </div>
        <div className="mt-6 space-y-6">
          {selectedOnboardedDevices.length > 0 ? (
            selectedOnboardedDevices.map((device) => (
              <ProgressBar
                key={device.deviceName}
                label={device.deviceName}
                value={device.count}
                totalValue={totalDevices}
              />
            ))
          ) : (
            <div className="flex items-center justify-center min-h-[300px] rounded-md">
              <EmptyTableRecord />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockProduct;
